import type { CSSProperties } from 'react';

/**
 * A record of variant names to their corresponding CSS styles.
 * Each key represents a variant name (e.g., 'initial', 'hover', 'focus'),
 * and the value is a CSSProperties object with the styles to apply.
 */
export type StyleVariants<T extends string = string> = Partial<
  Record<T, CSSProperties>
>;

/**
 * Merges CSS styles from multiple variants based on which variants are currently active.
 *
 * This function iterates through the `appliedVariants` array in order and merges
 * the corresponding styles from the `variants` object. Later variants in the array
 * override earlier ones, allowing for proper style cascading.
 *
 * @param variants - An object mapping variant names to their CSS styles
 * @param appliedVariants - An array of currently active variant names (order matters!)
 * @returns A merged object of CSS properties from all applied variants
 *
 * @example
 * ```ts
 * const variants = {
 *   initial: { backgroundColor: 'white', color: 'black' },
 *   hover: { backgroundColor: 'blue' },
 *   tap: { transform: 'scale(0.95)' }
 * };
 *
 * When hovering
 * getVariantStyles(variants, ['initial', 'hover']);
 * Returns: { backgroundColor: 'blue', color: 'black' }
 *
 * When tapping while hovering
 * getVariantStyles(variants, ['initial', 'hover', 'tap']);
 * Returns: { backgroundColor: 'blue', color: 'black', transform: 'scale(0.95)' }
 * ```
 */
export function getVariantStyles<T extends string>(
  variants: StyleVariants<T>,
  appliedVariants: T[]
): CSSProperties {
  const styles: CSSProperties = {};
  const nextStyles = styles as Record<
    string,
    CSSProperties[keyof CSSProperties]
  >;
  const initialVariant = 'initial' as T;
  const initialStyles = variants[initialVariant];
  const hasOwn = (obj: object, key: string) =>
    Object.prototype.hasOwnProperty.call(obj, key);

  for (const variant of appliedVariants) {
    const variantStyles = variants[variant];

    if (!variantStyles) {
      continue;
    }

    if (initialStyles && variant !== initialVariant) {
      for (const [property, initialValue] of Object.entries(initialStyles)) {
        const variantValue = variantStyles?.[
          property as keyof CSSProperties
        ] as CSSProperties[keyof CSSProperties] | undefined;

        if (hasOwn(variantStyles, property) && variantValue === undefined) {
          nextStyles[property] = initialValue;
        }
      }
    }

    for (const [property, value] of Object.entries(variantStyles)) {
      if (value !== undefined) {
        nextStyles[property] = value;
      }
    }
  }

  return styles;
}
